import { useClueWorkOrdersQuery } from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import RedAlert from '@/gf/components/RedAlert'
import Spinner from '@/gf/components/Spinner'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
} from '@/gf/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/gf/components/ui/popover'
import SelectorTrigger from '@/gf/components/ui/selector-trigger'
import useDebounce from '@/gf/hooks/useDebounce'
import { useMemo, useState } from 'react'

const ClueWorkOrderNumber = ({
  workOrderId,
  workOrderNumber,
  orgMachineId,
  onChange,
}: {
  workOrderId: string | undefined
  workOrderNumber?: string | null
  orgMachineId: string
  onChange: (workOrder: { id: string; number: string }) => void
}) => {
  const client = useGqlClient()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)
  // Ask Clue again on every open: the list from mount goes stale when the
  // buyer creates the work order in Clue with this page open (GEA-11277).
  // notifyOnNetworkStatusChange shows the spinner instead of the stale rows.
  const { data, loading, error, refetch } = useClueWorkOrdersQuery({
    client,
    variables: { orgMachineId },
    notifyOnNetworkStatusChange: true,
  })

  const onOpenChange = (next: boolean) => {
    setOpen(next)
    // A failed refetch shows through `error`; the promise has nothing to add.
    if (next) refetch().catch(() => undefined)
  }

  const workOrders = useMemo(() => {
    return data?.clueWorkOrders ?? []
  }, [data])

  // Show the human-readable WO Number, not the internal Clue id. Resolve from
  // the loaded list when possible; fall back to the parent-supplied number
  // (e.g. on edit, before the list loads, or if the WO has since closed and
  // dropped out of the open-WO list — mirrors the editing safeguard below).
  const selectedNumber =
    workOrders.find((wo) => String(wo.id) === String(workOrderId))?.number ??
    workOrderNumber ??
    undefined

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <SelectorTrigger value={selectedNumber} placeholder="Select work order" />
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start">
        <Command shouldFilter={true}>
          <CommandInput
            placeholder="Search work order..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {error ? (
              <RedAlert className="bg-transparent" title="Error loading work orders." />
            ) : loading ? (
              <CommandLoading>
                <div className="flex flex-row gap-x-2 items-center justify-center">
                  <Spinner className="mx-0" size="xs" color="blue" />
                  Searching work orders...
                </div>
              </CommandLoading>
            ) : !debouncedSearch ? (
              <CommandEmpty>Search for work orders.</CommandEmpty>
            ) : workOrders.length === 0 ? (
              <CommandEmpty>No work orders found.</CommandEmpty>
            ) : null}
            <CommandGroup>
              {workOrders.map((workOrder) => (
                <CommandItem
                  key={workOrder.id}
                  value={workOrder.number}
                  keywords={[
                    `${workOrder.id}`,
                    workOrder.number,
                    workOrder.title,
                    workOrder.status,
                  ]}
                  onSelect={() => {
                    setSearch('')
                    setOpen(false)
                    onChange({ id: String(workOrder.id), number: workOrder.number })
                  }}
                >
                  <div className="flex flex-col gap-y-0 items-start">
                    <span className="font-medium">{workOrder.title || workOrder.number}</span>
                    <span className="text-gray-700 text-sm">
                      ID: {workOrder.id}, Number: {workOrder.number}, Status: {workOrder.status}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default ClueWorkOrderNumber
